import {QuoteWrapper} from '../models/quote/quote-wrapper';
import {EnumQuoteType} from '../models/enum/enum-quote-type';
import {CmcQuote} from '../models/quote/cmc-quote';
import {EmployeeRoleEnum} from '../models/employee/employee-role.enum';
import {ServiceLine} from '../models/quote/service-line';
import {BillingLine} from '../models/billing-line/billing-line.model';
import {QuoteService} from '../models/quote/quote-service';
import {ExtraFee} from '../models/quote/extra-fee';
import {Coefficient, MultiServiceBoost} from '../../../../../rem-pct-engine/rem-pct-engine-core/src/main/ts';
import {CustomGroup} from '../models/quote/group/custom-group';
import {PricingItem} from '../models/quote/pricing-item/pricing-item';
import {EnumPricingItemType} from '../models/quote/pricing-item/pricing-item-type';
import {ConsumablePricingItem} from '../models/quote/pricing-item/consumable-pricing-item';
import {PackPricingItem} from '../models/quote/pricing-item/pack-pricing-item';
import {DevicePricingItem} from '../models/quote/pricing-item/device-pricing-item';
import {DeviceConsumablePricingItem} from '../models/quote/pricing-item/device-consumable-pricing-item';
import {DeviceConsumableWithSuppPricingItem} from '../models/quote/pricing-item/device-consumable-with-supp-pricing-item';
import {OptionPricingItem} from '../models/quote/pricing-item/option-pricing-item';
import {Corridor} from '../models/corridor/corridor';
import {Article} from '../models/quote/pricing-item/article';
import {cloneDeep} from 'lodash-es';
import {ProductLineModel} from '../models/opportunity-context/opportunity-context.model';

export class EPPClientPrototypeMapper {

    public static setQuoteWrapperPrototype = (quoteWrapper: any) => {
        quoteWrapper.type == EnumQuoteType.CMC ? Object.setPrototypeOf(quoteWrapper, CmcQuote.prototype) : Object.setPrototypeOf(quoteWrapper, QuoteWrapper.prototype);
        if (quoteWrapper.employeeRole != null) {
            Object.setPrototypeOf(quoteWrapper.employeeRole, EmployeeRoleEnum.prototype);
            quoteWrapper.copyWith({employeeRole: EmployeeRoleEnum.getEmployeeRoleEnumFromEforceRole(quoteWrapper.employeeRole.eForceRole)});
        }
        if (quoteWrapper.serviceLines !== undefined) {
            quoteWrapper.serviceLines.forEach((value, index, array) => {
                array[index] = EPPClientPrototypeMapper.setServiceLinePrototype(value);
            });
        }
        const newProductLineModel: ProductLineModel[] = [];
        quoteWrapper.expectedProductLines && quoteWrapper.expectedProductLines.forEach((value, index, array) => {
            newProductLineModel[index] = Object.setPrototypeOf({...value}, ProductLineModel.prototype);
        });
        quoteWrapper = quoteWrapper.copyWith({expectedProductLines: newProductLineModel});

        return quoteWrapper;
    };

    public static setServiceLinePrototype = (serviceLine: any) => {
        serviceLine = Object.setPrototypeOf(cloneDeep(serviceLine), ServiceLine.prototype);

        if (serviceLine.renewalQuoteService) {
            serviceLine = serviceLine.copyWith({renewalQuoteService: EPPClientPrototypeMapper.setQuoteServicePrototype(serviceLine.renewalQuoteService)});
        }
        if (serviceLine.existingQuoteService) {
            serviceLine = serviceLine.copyWith({existingQuoteService: EPPClientPrototypeMapper.setQuoteServicePrototype(serviceLine.existingQuoteService)});
        }
        if (serviceLine.newQuoteService) {
            serviceLine = serviceLine.copyWith({newQuoteService: EPPClientPrototypeMapper.setQuoteServicePrototype(serviceLine.newQuoteService)});
        }
        serviceLine.billingLines && serviceLine.billingLines.forEach((bl, index, billingLines) => {
            billingLines[index] = Object.setPrototypeOf(bl, BillingLine.prototype);
        });

        return serviceLine;
    };

    public static setQuoteServicePrototype = (quoteService: any) => {

        quoteService = Object.setPrototypeOf(cloneDeep(quoteService), QuoteService.prototype);
        quoteService.extraFees && quoteService.extraFees.forEach((extraFee, index, extraFees) => {
            extraFees[index] = Object.setPrototypeOf(extraFee, ExtraFee.prototype);
        });
        if (quoteService.coefficient) {
            quoteService = quoteService.copyWith({coefficient: Object.setPrototypeOf(quoteService.coefficient, Coefficient.prototype)});
        }
        if (quoteService.multiServiceBoost) {
            quoteService = quoteService.copyWith({multiServiceBoost: Object.setPrototypeOf(quoteService.multiServiceBoost, MultiServiceBoost.prototype)});
        }
        quoteService.customGroups.forEach(customGroup => {
            Object.setPrototypeOf(customGroup, CustomGroup.prototype);
            customGroup.pricingItems.forEach((pricingItem, index, pricingItems) => {
                pricingItems[index] = Object.setPrototypeOf(pricingItem, PricingItem.prototype);
                switch (pricingItems[index].pricingItemType) {
                    case EnumPricingItemType.CONSUMABLE: {
                        let consumablePricingItem: ConsumablePricingItem = Object.setPrototypeOf(pricingItem, ConsumablePricingItem.prototype);
                        pricingItems[index] = consumablePricingItem.copyWith({
                            consumable: EPPClientPrototypeMapper.setArticlePrototype(consumablePricingItem.consumable)
                        });
                        break;
                    }
                    case EnumPricingItemType.PACK: {
                        let packPricingItem: PackPricingItem = Object.setPrototypeOf(pricingItem, PackPricingItem.prototype);
                        pricingItems[index] = packPricingItem.copyWith({
                            device: EPPClientPrototypeMapper.setArticlePrototype(packPricingItem.device)
                        });
                        break;
                    }
                    case EnumPricingItemType.DEVICE: {
                        let devicePricingItem: DevicePricingItem = Object.setPrototypeOf(pricingItem, DevicePricingItem.prototype);
                        pricingItems[index] = devicePricingItem.copyWith({
                            device: EPPClientPrototypeMapper.setArticlePrototype(devicePricingItem.device)
                        });
                        break;
                    }
                    case EnumPricingItemType.DEVICE_CONSUMABLE: {
                        let deviceConsumablePricingItem: DeviceConsumablePricingItem = Object.setPrototypeOf(pricingItem, DeviceConsumablePricingItem.prototype);
                        pricingItems[index] = deviceConsumablePricingItem.copyWith({
                            device: EPPClientPrototypeMapper.setArticlePrototype(deviceConsumablePricingItem.device),
                            consumable: EPPClientPrototypeMapper.setArticlePrototype(deviceConsumablePricingItem.consumable),
                        });
                        break;
                    }
                    case EnumPricingItemType.DEVICE_CONSUMABLE_WITH_SUPP: {
                        let deviceConsumableWithSuppPricingItem: DeviceConsumableWithSuppPricingItem = Object.setPrototypeOf(pricingItem, DeviceConsumableWithSuppPricingItem.prototype);
                        pricingItems[index] = deviceConsumableWithSuppPricingItem.copyWith({
                            device: EPPClientPrototypeMapper.setArticlePrototype(deviceConsumableWithSuppPricingItem.device),
                            consumable: EPPClientPrototypeMapper.setArticlePrototype(deviceConsumableWithSuppPricingItem.consumable),
                            deviceConsumableCorridor: deviceConsumableWithSuppPricingItem.deviceConsumableCorridor ? Object.setPrototypeOf(deviceConsumableWithSuppPricingItem.deviceConsumableCorridor, Corridor.prototype) : null
                        });
                        break;
                    }
                    case EnumPricingItemType.OPTION: {
                        let optionPricingItem: OptionPricingItem = Object.setPrototypeOf(pricingItem, OptionPricingItem.prototype);
                        pricingItems[index] = optionPricingItem.copyWith({
                            option: EPPClientPrototypeMapper.setArticlePrototype(optionPricingItem.option)
                        });
                        break;
                    }
                }

            });
        });
        if (quoteService.globalCorridor) {
            Object.setPrototypeOf(quoteService.globalCorridor, Corridor.prototype);
        }
        if (quoteService.globalCorridorOnlyNegociable) {
            Object.setPrototypeOf(quoteService.globalCorridorOnlyNegociable, Corridor.prototype);
        }
        if (quoteService.corridorDevices) {
            Object.setPrototypeOf(quoteService.corridorDevices, Corridor.prototype);
        }
        if (quoteService.corridorConsumables) {
            Object.setPrototypeOf(quoteService.corridorConsumables, Corridor.prototype);
        }


        return quoteService;
    };

    public static setArticlePrototype(article: Article) {
        Object.setPrototypeOf(article, Article.prototype);
        return article.copyWith({
            corridor:
                article.corridor ? Object.setPrototypeOf(article.corridor, Corridor.prototype) : null
        });
    }
}
